import {
    IconBook,
    IconClients,
    IconTokens,
    IconLive,
    IconNote,
    IconOperation,
    IconProjects,
    IconTasks,
    IconUsers,
} from '../../common/icons';

// The sidebar's contents, in one place. Adding a section to the panel is
// adding a row here plus its route in app/app.jsx — nothing else.
//
// `domain` is the permission the BACKEND requires on that resource's list
// route, not a name invented here: an entry the API would answer 403 to has
// no business being visible. They come from core/authz/roles.js.
//
// TWO GROUPS AND NOT ELEVEN ENTITIES. The rail used to enumerate the schema —
// clients, users, teams, tokens, projects, repositories, tiers, tasks — which
// answers "what tables are there" and not "what am I doing". The daily work is
// separated from the administration of the shop, and the project is the way in:
// its tiers and its breakdown live inside it, not as a parallel top-level list.
//
// TIERS IS DELIBERATELY NOT A ROW. A tier belongs to a project and is read from
// its workspace, where its plan, its agreement and its proposals are. Its own
// routes still exist and still work; what it lost is a place in the rail that
// suggested tiers are a pile you browse.
export const MENU = [
    {
        title: 'Trabajo',
        items: [
            // The way in. A person opens a project and finds everything about
            // it there: what is planned, how it was split, what is pending.
            { title: 'Proyectos',    path: '/admin/projects',     domain: 'projects:read',     Icon: IconProjects },
            // Va antes que Tareas porque va antes en el recorrido: de un plan
            // aprobado sale un tier, y de un tier acordado salen las tareas.
            // `plans:read` es su código propio — lo tienen admin, technician y
            // agent_master, y NO el esclavo.
            { title: 'Planeación',   path: '/admin/plans',        domain: 'plans:read',        Icon: IconNote },
            // UNA ENTRADA, TRES PESTAÑAS. Tareas, Revisión y Hallazgos son tres
            // momentos del mismo trabajo, y la prueba de que son uno es que LAS
            // TRES LEEN `GET /admin/tasks` con distinto filtro. Como tres filas
            // del riel obligaban a salir de una para mirar la otra, perdiendo el
            // proyecto elegido en el camino.
            //
            // `tasks:read` para las tres: no hay código `findings:*` ni
            // `reviews:*` en core/authz/roles.js, y un hallazgo ES una tarea.
            { title: 'Desarrollo',   path: '/admin/development',  domain: 'tasks:read',        Icon: IconTasks },
            // DOCUMENTACIÓN, ENTRE HACER Y OPERAR: lo que un proyecto dejó escrito
            // sobre sí mismo, empezando por su API reference. Por proyecto,
            // siempre — un documento es de un proyecto en Core.
            // `documents:read` es lo que piden la lista y las versiones.
            { title: 'Documentación', path: '/admin/documentation', domain: 'documents:read',   Icon: IconBook },
            // DESPUÉS DE DESARROLLO PORQUE VA DESPUÉS EN EL RECORRIDO: lo que
            // se planeó, se partió y se hizo, en algún momento alguien lo usa y
            // dice algo. Operación es esa vuelta.
            //
            // Y es una sección y no una pestaña de Desarrollo: un ticket NO es
            // una tarea y no se convierte en una —el tier lo prohíbe con todas
            // las letras— así que compartir pantalla con la cola de trabajo
            // habría sugerido lo contrario.
            //
            // TRES VENTANAS, NO UNA: tickets —lo que la gente reportó—, logs
            // —lo que la máquina registró— y changelogs —qué cambió y cuándo—.
            // Son tres registros de la misma pregunta: cómo se está portando lo
            // que ya está andando. Hoy solo está construida la de tickets, y la
            // sección lo dice en el pie en vez de parecer completa.
            //
            // `tasks:read` porque es lo que pide el backend HOY, que es lo único
            // que esta entrada puede prometer: no hay código `tickets:*` en
            // core/authz/roles.js, las cuatro rutas de tickets piden los
            // permisos de tarea. Cuando lleguen los logs y los changelogs con
            // sus propios códigos, esto pasa a ser un `any: [...]` como el de
            // Usuarios y equipos, y cada ventana se dibuja o no según el suyo.
            { title: 'Operación',    path: '/admin/operation',    domain: 'tasks:read',        Icon: IconOperation },
            // Reads the event log, so `events:read` and not `tasks:read`: the
            // pulse shows every movement the account may see, and a task is
            // only one of the things that move.
            { title: 'Agentes', path: '/admin/agents', any: ['events:read', 'machines:read'], Icon: IconOperation },
            { title: 'En vivo',      path: '/admin/live',         domain: 'events:read',       Icon: IconLive },
        ],
    },
    {
        title: 'Administración',
        items: [
            { title: 'Estándares', path: '/admin/standards', domain: 'standards:read', Icon: IconBook },
            { title: 'Clientes',     path: '/admin/clients',      domain: 'clients:read',      Icon: IconClients },
            // ONE ENTRY, TWO TABS. People and teams are the same question asked
            // twice — who works here, and how they are grouped — and the answer
            // to one is read while looking at the other: which teams is this
            // person on, who is on this team.
            //
            // `any` and not `domain`: they are two permission codes and holding
            // either one is enough to open the section. The tab whose code is
            // missing is not drawn. Somebody with only `teams:read` gets the
            // section with the Equipos tab and nothing else.
            {
                title: 'Usuarios y equipos',
                path: '/admin/people',
                any: ['users:read', 'teams:read'],
                Icon: IconUsers,
            },
            // `tokens:admin` and not `tokens:read`: issuing mints a credential,
            // and the list itself already tells you who has an agent and when it
            // last ran. All three token routes ask for the same one.
            { title: 'Tokens y agentes', path: '/admin/tokens',   domain: 'tokens:admin',      Icon: IconTokens },
        ],
    },
];

// Drops the entries this account cannot open, and then the groups left empty.
// A group heading with nothing under it is worse than no heading: it says
// something is hidden without saying what.
//
// TSS vite/05 — can() outside JSX is allowed, and this is the shape it is for:
// a builder that returns a filtered array. Inside JSX it would be <Gate>.
//
// An entry declares EITHER `domain` (one code) OR `any` (a list, of which one
// is enough). The second exists for sections that merge two resources: the
// section opens if you can read either of them, and the part you cannot read is
// what the screen leaves out.
export function canOpen(item, can) {
    if (item.any) return item.any.some(code => can(code));
    return can(item.domain);
}

export function visibleMenu(can) {
    return MENU
        .map(group => ({ ...group, items: group.items.filter(item => canOpen(item, can)) }))
        .filter(group => group.items.length > 0);
}

// WHERE `/` GOES, and why it is computed instead of hardcoded.
//
// It used to redirect to /admin/clients, which is wrong twice: it is not where
// anybody starts working, and an account without `clients:read` landed on the
// unauthorized screen every single time it opened the panel. Sending somebody
// to a door they cannot open is not a default, it is a bug with a fixed
// address.
//
// So: the first entry this account can actually read, in rail order — which
// puts Proyectos first for everyone who can see it. `/admin/profile` is the
// floor, because everybody who can log in has an account.
export function landingPath(can) {
    for (const group of MENU) {
        for (const item of group.items) {
            if (canOpen(item, can)) return item.path;
        }
    }
    return '/admin/profile';
}
